'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'

import { useCartStore } from 'modules/cart'
import { AnyProductTypes } from 'modules/products'
import { isNilOrEmpty } from 'shared/utils/ramda'

import { ActivePromotionState , PromotionType } from '../../types'
import { checkIfPromotionIsActive, deliveryPromotionKeyword } from '../../utils'

import SpecialPromotionLabel from './components/SpecialPromotionLabel'
import SpecialPromotionModal from './components/SpecialPromotionModal'

const SpecialPromotion = ({
  source,
  product,
  notClickable
}: {
  source?: string
  product: AnyProductTypes
  notClickable?: boolean
}) => {
  const promotion = product?.promotions?.[0]

  const [open, setOpen] = useState(false)
  const [activePromotion, setActivePromotion] =
    useState<ActivePromotionState | null>(null)

  const { activeSpecialPromotions, cart } = useCartStore()

  const isActivePromotion = useMemo(
    () =>
      promotion &&
      checkIfPromotionIsActive(promotion, cart, activeSpecialPromotions),
    [promotion, cart, activeSpecialPromotions]
  )

  useEffect(() => {
    if (!promotion) return
    const activeSpecialPromotion = activeSpecialPromotions.find(
      promo => promo.promotionId === promotion.id
    )

    setActivePromotion({
      quantity: activeSpecialPromotion?.activePromotionsQuantity,
      freeDelivery: cart?.isEligibleForFreeDelivery,
      type: promotion.type.includes(deliveryPromotionKeyword)
        ? PromotionType.FREE_DELIVERY
        : PromotionType.NORMAL
    })
  }, [activeSpecialPromotions, promotion?.id, cart])

  const handleLabelClick = useCallback((event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setOpen(true)
  }, [])

  const handleClose = useCallback(() => {
    setOpen(prev => !prev)
  }, [])

  const handleEvent = useCallback((event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
  }, [])

  if (!promotion || isNilOrEmpty(promotion) || promotion?.isSoldOut) return null

  return (
    <div onClick={handleEvent}>
      <SpecialPromotionLabel
        promotion={promotion}
        source={source}
        notClickable={notClickable}
        isActivePromotion={Boolean(isActivePromotion)}
        activePromotion={activePromotion}
        onClick={handleLabelClick}
      />
      <SpecialPromotionModal
        open={open}
        handleClose={handleClose}
        promotion={promotion}
      />
    </div>
  )
}

export default SpecialPromotion
